import React, { useState } from "react";
import { Alert, Grid, Button } from "@mui/material";
import axios from "axios";

import { config } from "src/constants";
import { useNavigate } from "react-router-dom-v5-compat";

import { Link as RouterLink } from "react-router-dom-v5-compat";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Typography,
} from "@mui/material";
import Logo from "src/components/Logo";
import PasswordInput from "@rlmonger/web_components/dist/Inputs/Password";
import PasswordRequirements, {
  isPasswordPolicySatisfied,
} from "@rlmonger/web_components/dist/Inputs/PasswordPolicy";

const RecoverPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [historyStatus, setHistoryStatus] = useState("neutral");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    severity: "info",
    message: "",
  });

  const navigate = useNavigate();

  const resetPassword = async () => {
    if (
      isSubmitting ||
      !isPasswordPolicySatisfied(password) ||
      password !== confirmPassword
    ) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token?") || params.get("token");

    if (!token) {
      setAlert({
        show: true,
        severity: "error",
        message: "No login token can be found",
      });
      return;
    }

    setIsSubmitting(true);
    setAlert({
      show: true,
      severity: "info",
      message: "Logging in with token...",
    });

    try {
      const { data: user } = await axios.get(`${config.api}/auth/tokenLogin`, {
        params: {
          token,
        },
      });

      if (!user.token) {
        throw new Error("Something went wrong whilst trying to log in");
      }

      axios.defaults.headers.common["Authorization"] = `Bearer ${user.token}`;

      await axios.post(`${config.api}/user/savenewpassword`, {
        password,
      });

      setAlert({
        show: true,
        severity: "success",
        message: "Successfully changed password! Redirecting...",
      });
      navigate("/");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to reset password; the login token might have expired";
      if (
        error?.response?.data?.code === "PASSWORD_REUSED" ||
        /previous\s+(5|five)\s+passwords/i.test(errorMessage)
      ) {
        setHistoryStatus("failed");
      }
      setAlert({
        show: true,
        severity: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Recover Password | MongerTracker GPS Tracking</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="sm" sx={{ py: "80px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 8,
            }}
          >
            <RouterLink to="/">
              <Logo
                color="black"
                sx={{
                  height: 40,
                  width: 40,
                }}
              />
            </RouterLink>
          </Box>
          <Card>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                p: 4,
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <div>
                  <Typography color="textPrimary" gutterBottom variant="h4">
                    Recover Password
                  </Typography>
                  {/* <Typography color="textSecondary" variant="body2">
                    
            </Typography>*/}
                </div>
              </Box>
              <Grid container direction="column" spacing={3}>
                <Grid item>
                  <PasswordInput
                    style={{ width: "100%" }}
                    formControlStyle={{ width: "100%" }}
                    label="New Password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setHistoryStatus("neutral");
                    }}
                  />
                </Grid>
                <Grid item>
                  <PasswordInput
                    style={{ width: "100%" }}
                    formControlStyle={{ width: "100%" }}
                    label="Confirm New Password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  <PasswordRequirements
                    password={password}
                    confirmPassword={confirmPassword}
                    includeHistory
                    historyStatus={historyStatus}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    onClick={resetPassword}
                    disabled={
                      isSubmitting ||
                      !isPasswordPolicySatisfied(password) ||
                      password !== confirmPassword
                    }
                  >
                    {isSubmitting ? "Resetting Password…" : "Reset Password"}
                  </Button>
                </Grid>
                <Grid item>
                  <Alert severity={alert.severity} hidden={!alert.show}>
                    {alert.message}
                  </Alert>
                </Grid>
              </Grid>
              <Divider sx={{ my: 3 }} />
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default RecoverPassword;
